/**
 * Politique de réessai des requêtes API.
 *
 * Le 503 que renvoie nginx quand `limit_req` refuse une requête n'est pas une
 * panne : c'est un « reviens dans un instant ». Y répondre par un échec sec
 * cassait l'UX et remplissait Sentry, alors qu'une seconde tentative aboutit
 * presque toujours.
 *
 * Le jitter n'est pas une coquetterie. Les requêtes refusées le sont par
 * rafales — une vingtaine de tuiles d'un même déplacement de carte. Si toutes
 * réessaient après le même délai fixe, elles reforment la rafale à l'identique
 * et se refont refuser ensemble. Les étaler est ce qui casse le cycle.
 */

/** Une tentative initiale + deux réessais. */
export const MAX_ATTEMPTS = 3
export const BASE_DELAY_MS = 300
export const MAX_DELAY_MS = 4000

const METHODES_IDEMPOTENTES = new Set(['get', 'head', 'options'])

/**
 * Une méthode rejouable sans effet de bord.
 *
 * Axios laisse `method` indéfini pour un GET, d'où le défaut.
 */
export function estIdempotente(method) {
  return METHODES_IDEMPOTENTES.has(String(method ?? 'get').toLowerCase())
}

/**
 * Faut-il rejouer cette requête ?
 *
 * `attempt` est le numéro de la tentative qui vient d'échouer (1 = la première).
 *
 * Sans `status`, il n'y a pas eu de réponse : réseau coupé, DNS, timeout. On ne
 * réessaie pas — cela ne ferait que retarder de plusieurs secondes un échec
 * que l'utilisateur constate déjà.
 */
export function doitReessayer({ method, status, attempt }) {
  if (attempt >= MAX_ATTEMPTS) return false
  if (!estIdempotente(method)) return false
  if (typeof status !== 'number') return false
  return status === 429 || (status >= 500 && status < 600)
}

/**
 * Délai avant la prochaine tentative, en millisecondes.
 *
 * Jitter « égal » — l'aléa porte sur la moitié haute de la fenêtre, jamais sur
 * la totalité : un réessai tiré à 0 ms contre un limiteur de débit consomme un
 * jeton de plus sans aucune chance d'aboutir.
 *
 * `alea` est injectable pour que les tests portent sur des bornes, pas sur un
 * tirage.
 */
export function delaiAvantReessai({ attempt, retryAfterMs = null, alea = Math.random }) {
  // Un Retry-After explicite fait autorité : le serveur sait mieux que nous.
  if (typeof retryAfterMs === 'number' && retryAfterMs >= 0) {
    return Math.min(retryAfterMs, MAX_DELAY_MS)
  }
  const fenetre = Math.min(BASE_DELAY_MS * 2 ** (attempt - 1), MAX_DELAY_MS)
  return Math.round(fenetre / 2 + alea() * (fenetre / 2))
}

/**
 * Lit l'en-tête `Retry-After`, dans ses deux formes normalisées (RFC 9110) :
 * un nombre de secondes, ou une date HTTP.
 *
 * Rend `null` si l'en-tête est absent, illisible, ou déjà dépassé — auquel cas
 * l'appelant retombe sur le backoff exponentiel.
 */
export function lireRetryAfter(headers) {
  const brut = headers?.['retry-after'] ?? headers?.['Retry-After']
  if (brut === undefined || brut === null || brut === '') return null

  const secondes = Number(brut)
  if (Number.isFinite(secondes)) {
    return secondes >= 0 ? secondes * 1000 : null
  }

  const date = Date.parse(brut)
  if (Number.isNaN(date)) return null

  const attente = date - Date.now()
  return attente > 0 ? attente : null
}
